import { FieldType } from 'bindings/forms/FieldType';
import { ChevronDown, ChevronUp, LockKeyhole, Palette, Settings, Trash2 } from 'lucide-react';
import equal from 'deep-equal';
import DragAndDropImage from 'images/icons/drag_and_drop_dots.svg';
import * as React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Prompt } from 'react-router';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import styled from 'styled-components';
import { AAdditionalColumns, ADDITIONAL_COLUMN_TYPE_LST, AdditionalColumn as GlobalAdditionalColumn, getAdditionalColumns } from '../../../atoms/additionalColumns';
import { AAdditionalFieldColumns, AdditionalFieldColumn as GlobalAdditionalFieldColumn, getAdditionalFieldColumns } from '../../../atoms/additionalFieldColumns';
import { ACalculatedFields, CalculatedField as CalculatedFieldAtom, CalculatedFieldTarget, CalculatedField as GlobalCalulatedField, getCalculatedFields, isPromotionCalculatedField } from '../../../atoms/calculatedFields';
import { AColors } from '../../../atoms/colors/colors';
import { AFormFields, Field, getFields } from '../../../atoms/forms';
import { isOwner, useMe } from '../../../atoms/global/users';
import Add from '../../../components_v2/add/Add';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import DropdownColor from '../../../components_v2/dropdown/DropdownColors';
import { blurOnEnterPressed } from '../../../components_v2/input/Input';
import { PopupMode } from '../../../components_v2/popup/model/Model';
import Popup from '../../../components_v2/popup/Popup';
import PopupCreation, { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import { Arrow } from '../../../components_v2/table/style/Style';
import { getTags } from '../../../components_v2/toolbarFilter/tags/action';
import { TagType, tagTypeToAdditionalFieldColumnType } from '../../../components_v2/toolbarFilter/tags/Model';
import { diff, isSuperAdmin } from '../../../components_v2/utils';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import {
	PrimaryColor,
	BorderColor,
	DarkGreySidely2,
	FilterLightGrey,
	FilterRed,
	RedSidely,
	SidelyBlack,
	SurfaceColor
} from '../../../styles/global/css/Utils';
import { Translate, translateToNode, translateToString } from '../../../styles/global/translate';
import { useEditFormsPermission } from '../../../utils/customHooks';
import { CreationPopupContainer } from '../../../components_v2/popup/style/CreationFormStyle';
import { SelectMetaData } from '../../forms/PopupField';
import { launchToastMessage } from '../../forms/visual-form-editor/visualFormEditor';
import { ToolbarState } from '../../globals/mainPage/mainPage';
import { LoadingStateEnum } from '../../import/model';
import { ComponentLoader } from '../../map/modalRight/ModalCalendar';
import { getOpportunityStatuses } from '../../opportunities/data/Data';
import { getOrderStatuses } from '../../orders/data/Data';
import Restricted from '../../permissions/Restricted';
import RestrictedSuperAdmin from '../../permissions/RestrictedSuperAdmin';
import usePermission from '../../permissions/usePermission';
import { ModalState } from '../../products/model';
import { FlexDiv } from '../../products/style';
import { ReportFiltersProvider } from '../../reports/generic/generic';
import { GenericEditorProvider } from '../../reports/generic/genericEditor';
import { AdditionalColumnsDisplayOptions, PinStyle, getClientStatuses, syncTagsAndStatuses } from './actions';
import { CalculatedFieldsCreation } from './CalculatedFieldsCreation';
import MapColorsPopup from '../../../components_v2/colorScale/MapColorsPopup';
import { ColumnDisplayOptions, MapColorsKind, mapColorsKindOf } from '../../map/colorScale';
import ObjectiveEditor from './ObjectiveEditor';
import ScorecardEditor from './ScorecardEditor';
import { CompanyProfile } from './CompanyProfile';
import OpeningHoursRuleEditor from './openingHoursRule';
import { SystemSettingTitle, SystemSettingsWrapper } from './styles';

export type Id = number | { unsync: number }

type Status<T = unknown> = T & { deleted?: boolean, color_code: string, id: Id, name?: string, sequence?: number, locked?: boolean, probability?: number };
type Tag<T = unknown> = T & { deleted?: boolean, id: Id, name?: string, color: string };
type Color<T = unknown> = T & { colorCode: string, id: number };
type AdditionalColumn = Pick<GlobalAdditionalColumn, 'type' | 'data' | 'ishidded' | 'field_id'> & {
	id: Id,
	locked: boolean,
	name?: string,
	deleted?: boolean,
	display_options?: AdditionalColumnsDisplayOptions
}
type AdditionalFieldColumn = Pick<GlobalAdditionalFieldColumn, 'field_id' | 'field_name' | 'ishidded'> & {
	id: Id,
	deleted?: boolean,
	field_type?: FieldType,
	display_options?: ColumnDisplayOptions,
}

type CalculatedField = Omit<GlobalCalulatedField, 'id'> & { id: Id };

// the map colours popup of a column row, with what applying its result
// does to that row: the popup hands the row's options back, colours changed
type MapColorsEdition = {
	isOpen: boolean,
	title?: string,
	kind?: MapColorsKind,
	date?: boolean,
	options?: ColumnDisplayOptions,
	apply?: (options: ColumnDisplayOptions | undefined) => void,
};

export const StatusWrapper = styled.div`
	display: flex;
	gap: 0.7rem;
	align-items: center;
`;

export const StatusContainer = styled.div<{ unsync: boolean, width?: string }>`
	display: flex;
	width: ${p => p.width ?? '350px'};
	height: 45px;
	align-items: center;
	gap: 20px;
	border: 1px ${p => p.unsync ? 'dashed' : 'solid'} ${BorderColor};
	border-radius: 5px;
	padding: 0 2rem;
	background-color: ${SurfaceColor};
	position: relative;

`;

const TagContainer = styled(StatusContainer) <{ borderColor?: string }>`
	padding: 5px 10px 5px 15px;
	gap: 10px;
	width: fit-content;
	height: unset;
	border-color: ${p => p.borderColor ?? BorderColor};
`;

export const DandDDots = styled.div<{ height?: string, width?: string }>`
	width: ${p => p.width ?? '1rem'};
	height: ${p => p.height ?? '100%'};
	background-image: url('${DragAndDropImage}');
	background-position: center;
	background-repeat: no-repeat;
	background-size: 50%;
`;

export const TextStatusWrapper = styled.div`
	width: inherit;
	white-space: nowrap;
	overflow: hidden;
	cursor: text;
`;

const TextTagWrapper = styled(TextStatusWrapper)`
	width: fit-content;
`;

const Lock = styled.div`
	cursor: not-allowed;
`;

export const PreventModif = styled.div<{ locked: boolean } >`
	cursor: pointer;
	filter: ${p => p.locked ? 'none' : FilterLightGrey};
`;

export const PointerDiv = styled.div`
	cursor: pointer;
`;

export const WarningLock = styled.div`
	cursor: help;
	filter: ${FilterRed};
`;

const StatusProbabilityWrapper = styled.div<{ locked?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-shrink: 0;
	${p => p.locked ? 'width: 5em;' : `
		width: 6em;
		padding: 0 1em;
		border-bottom: 1px solid ${SidelyBlack};
	`}
`;

export const InputStatusName = styled.input<{ unsync: boolean, nameLength: number, disabled?: boolean }>`
	border: none;
	border-bottom: 1px solid transparent;
	&:hover {
		border-color: ${PrimaryColor};
	}
	&:focus {
		border-color: ${PrimaryColor};
	}
	width: ${p => p.nameLength + 1}ch;
	min-width: 20ch;
	${p => p.unsync ? 'opacity: 80%;' : ''}
	&:read-only {
		border-color: transparent;
	}
	background-color: ${p => p.disabled ? 'transparent' : SurfaceColor};
`;

const InputTagName = styled(InputStatusName)`
	min-width: 12ch;
`;

export const SaveButton = styled(DefaultButton)`
	width: 90px;
	margin: 5px;
`;

export const SettingIconDiv = styled.div`
	cursor: pointer;
`;

const TextButton = styled.button`
	background: none;
	border: none;
	text-decoration: underline;
`;

const tagTypeToString = (type: TagType): string => {
	switch (type) {
		case TagType.COMPANY: return 'company';
		case TagType.CONTACT: return 'contact';
		case TagType.OPPORTUNITY: return 'opportunity';
		case TagType.ORDER: return 'order';
		case TagType.PRODUCT: return 'product';
	}
};

export type CreationEditionOrDeletion<A extends object = object, B extends object = object> = { Creation: Omit<A, 'id'> } | { Edition: { id: number | string, value: Omit<B, 'id'> } } | { Deletion: number | string }

export function valueToCreationEditionOrDeletion<T extends { id: Id, deleted?: boolean }>(value: T): CreationEditionOrDeletion<T, T> {
	if (value.deleted && typeof value.id === 'number') {
		return {
			Deletion: value.id
		};
	}
	if (value.deleted) throw 'deletion of uncreated value';
	if (typeof value.id === 'number') {
		return {
			Edition: {
				id: value.id,
				value: { ...value, id: undefined }
			}
		};
	}
	return {
		Creation: { ...value, id: undefined }
	};
}

async function saveChanges(statusDifferences: Status[], tagDifferences: Tag[], colors: Color[], field_type: TagType, additionalColumnDifferences: AdditionalColumn[], additionalFieldColumnDifferences: AdditionalFieldColumn[], calculatedFieldsDifferences: CalculatedField[]): Promise<void> {
	function convert<T extends(object & { id: Id })>(t: T) {
		const value = { ...t };
		if ('color_code' in t && t.color_code) {
			value['color_status_id'] = colors.find(c => c.colorCode === t.color_code)?.id;
		} else if ('color' in t && t.color) {
			value['color_status_id'] = colors.find(c => c.colorCode === t.color)?.id;
		}
		if ('name' in t && t.name && typeof t.name === 'string') {
			value['name'] = t.name.trim();
		}
		return valueToCreationEditionOrDeletion(value);
	}

	function convertAdditionnalColumn<T extends(object & { id: Id })>(t: T) {
		const value = { ...t };
		let field_id: any = 'unmodified';
		if ('color_code' in t && t.color_code) {
			value['color_status_id'] = colors.find(c => c.colorCode === t.color_code)?.id;
		} else if ('color' in t && t.color) {
			value['color_status_id'] = colors.find(c => c.colorCode === t.color)?.id;
		}
		if ('name' in t && t.name && typeof t.name === 'string') {
			value['name'] = t.name.trim();
		}
		if ('field_id' in t) {
			field_id = t.field_id === null ? 'deleted' : t.field_id === undefined ? 'unmodified' : { updated: t.field_id };
		}
		value['field_id'] = field_id;
		return valueToCreationEditionOrDeletion(value);
	}

	return syncTagsAndStatuses({
		statuses: statusDifferences.map(convert),
		tags: tagDifferences.map(convert),
		additional_columns: additionalColumnDifferences.map(convertAdditionnalColumn),
		additional_field_columns: additionalFieldColumnDifferences.map(afc => valueToCreationEditionOrDeletion({ ...afc, field_name: undefined, type: tagTypeToAdditionalFieldColumnType(field_type) })),
		calculated_fields: calculatedFieldsDifferences.map(convert),
		field_type
	});
}

function zipById<A extends unknown & { id: Id }, B extends unknown & { id: Id }>(a: A[], b: B[]): [A | undefined, B | undefined][] {
	const base: [A | undefined, B | undefined][] = a.map(a_child => [a_child, b.find(b => b.id === a_child.id)]);
	b.forEach(b_child => {
		if (base.some(base_child => base_child[1]?.id === b_child.id)) return;
		base.push([undefined, b_child]);
	});
	return base;
}

export function getIdListDifferences<T extends unknown & { id: Id }>(a: T[], b: T[]): T[] {
	return zipById(a, b)
		.reduce((acc, [a, b]) => {
			const differences = diff(a, b);
			if (!differences || equal(differences, {})) return acc;
			return [...acc, { ...differences, id: a?.id ?? b?.id }];
		}, []);
}

function getIdListDifferencesAdditionalColumns<T extends unknown & { id: Id }>(a: T[], b: T[]): T[] {
	return zipById(a, b)
		.reduce((acc, [a, b]) => {
			const differences = diff(a, b);
			if (!differences || equal(differences, {})) return acc;
			// the display options are one json the backend replaces whole: a
			// change inside (a pin style, a colour stop) sends them whole
			if ('display_options' in differences && b && 'display_options' in b && typeof differences.display_options === 'object' && typeof b.display_options === 'object') {
				differences.display_options = { ...b.display_options };
			}
			return [...acc, { ...differences, id: a?.id ?? b?.id }];
		}, []);
}

/**
 * Differences whose `report` and `display_options` are sent whole: the
 * server replaces them wholesale, and a partial nested diff shallow-merged
 * used to drop untouched keys (an edited external field lost its dataset
 * and measure; a colour scale would lose its stops).
 */
export function getIdListDifferencesWholeKeys<T extends unknown & { id: Id }>(a: T[], b: T[]): T[] {
	return zipById(a, b)
		.reduce((acc, [a, b]) => {
			const differences = diff(a, b);
			if (!differences || equal(differences, {})) return acc;
			for (const key of ['report', 'display_options']) {
				if (key in differences && b && key in b) {
					differences[key] = b[key];
				}
			}
			return [...acc, { ...differences, id: a?.id ?? b?.id }];
		}, []);
}

export default function StatusAndTagsSettings(props: {
	type: TagType,
	setToolBarState: (state: ToolbarState) => void
}): JSX.Element {
	const [calculatedFields, setCalculatedFields] = React.useState<string>(JSON.stringify({
		'group_by': 'Company',
		'panels': [
			{ 'columns': [], products: undefined, name: '' }
		],
		'range': null,
		'window': null,
		'evolution': null,
		'variation': null,
		'filter': null
	}));

	return <>
		<GenericEditorProvider report={calculatedFields} setReport={setCalculatedFields}>
			<ReportFiltersProvider genericReport={JSON.parse(calculatedFields)}>
				<_StatusAndTagsSettings {...props} />
			</ReportFiltersProvider>
		</GenericEditorProvider>
	</>;
}

function copyToClipboard(id: number | string) {
	navigator.clipboard.writeText(id.toString());
	launchToastMessage('Id copié dans le presse-papier');
}

function _StatusAndTagsSettings(props: {
	type: TagType,
	setToolBarState: (state: ToolbarState) => void
}): JSX.Element {
	const [subSettings, setSubSettings] = React.useState<Array<'tags' | 'statuses' | 'additional_columns' | 'additional_field_columns' | 'report_fields' | 'calculated_fields'>>([]);
	const [tags, setTags] = React.useState<Tag[]>([]);
	const [defaultTags, setDefaultTags] = React.useState<Tag[]>([]);
	const [statuses, setStatuses] = React.useState<Status[]>([]);
	const [defaultStatuses, setDefaultStatuses] = React.useState<Status[]>([]);

	const [additionalColumns, setAdditionalColumns] = React.useState<AdditionalColumn[]>([]);
	const [defaultAdditionalColumns, setDefaultAdditionalColumns] = React.useState<AdditionalColumn[]>([]);
	const setAdditionalColumnsAtom = useSetRecoilState(AAdditionalColumns);

	const [additionalFieldColumns, setAdditionalFieldColumns] = React.useState<AdditionalFieldColumn[]>([]);
	const [defaultAdditionalFieldColumns, setDefaultAdditionalFieldColumns] = React.useState<AdditionalFieldColumn[]>([]);
	const fields = useRecoilValue(AFormFields);
	const colors = useRecoilValue(AColors);
	const [loadingState, setLoadingState] = React.useState(LoadingStateEnum.LOADING);

	const [additionalColumnSettingState, setAdditionalColumnSettingState] = React.useState<ModalState<AdditionalColumn>>({ isOpen: false });

	const setAdditionalFieldColumnsAtom = useSetRecoilState(AAdditionalFieldColumns);

	const setCalculatedFieldsAtom = useSetRecoilState(ACalculatedFields);
	const [isCalculatedFieldsOpen, setIsCalculatedFieldsOpen] = React.useState(false);
	const [isScorecardOpen, setIsScorecardOpen] = React.useState(false);
	const [isObjectiveOpen, setIsObjectiveOpen] = React.useState(false);
	const [calculatedFields, setCalculatedFields] = React.useState<CalculatedField[]>([]);
	const [newCalculatedField, setNewCalculatedField] = React.useState<CalculatedField | null>(null);
	const [defaultCalculatedFields, setDefaultCalculatedFields] = React.useState<CalculatedField[]>([]);
	const [calculatedFieldToEdit, setCalculatedFieldToEdit] = React.useState<CalculatedField | null>(null);
	const [mapColorsState, setMapColorsState] = React.useState<MapColorsEdition>({ isOpen: false });
	const [isEditMode, setIsEditMode] = React.useState(false);
	const [isCompanyOverviewOpen, setIsCompanyOverviewOpen] = React.useState(false);

	const status_differences = getIdListDifferences(defaultStatuses, statuses);
	const tag_differences = getIdListDifferences(defaultTags, tags);
	const additional_column_differences = getIdListDifferencesAdditionalColumns(defaultAdditionalColumns, additionalColumns);
	const additional_field_column_differences = getIdListDifferencesWholeKeys(defaultAdditionalFieldColumns, additionalFieldColumns);
	const calculated_field_differences = getIdListDifferencesWholeKeys(defaultCalculatedFields, calculatedFields);
	const hasDifferences = status_differences.length !== 0 || tag_differences.length !== 0 || additional_column_differences.length != 0 || additional_field_column_differences.length != 0 || calculated_field_differences.length != 0;
	const me = useMe();
	const superAdmin = isSuperAdmin();
	const isAllowedToEditForms = useEditFormsPermission();
	// hoisted out of the rows: the calculated fields now render as three lists,
	// and a hook called once per row runs a different number of times as rows
	// move between them
	const mayCreateSettings = usePermission({ objectAction: 'CreateCompanySettings' });
	const mayUpdateSettings = usePermission({ objectAction: 'UpdateCompanySettings' });
	const mayEditCalculatedFields = isOwner(me) || superAdmin;

	// three lists, three kinds of definition: a report field is written in the
	// report editor, a scorecard in the scorecard editor and an objective
	// completion in its own, and none of the three can read another's
	const byName = (a: CalculatedField, b: CalculatedField) => a.field_name.localeCompare(b.field_name);
	const aliveCalculatedFields = calculatedFields.filter(cf => !cf.deleted);
	const reportFields = aliveCalculatedFields
		// the promotion fields have their own page (Champs de campagne)
		.filter(cf => cf.target !== CalculatedFieldTarget.Score && cf.target !== CalculatedFieldTarget.Objective && !isPromotionCalculatedField(cf))
		.sort(byName);
	const scorecardFields = aliveCalculatedFields.filter(cf => cf.target === CalculatedFieldTarget.Score).sort(byName);
	const objectiveFields = aliveCalculatedFields.filter(cf => cf.target === CalculatedFieldTarget.Objective).sort(byName);

	function editCalculatedField(cf: CalculatedField, open: (value: boolean) => void): void {
		if (!mayEditCalculatedFields) return;
		setIsEditMode(true);
		setCalculatedFieldToEdit(cf);
		open(true);
	}

	function createCalculatedField(open: (value: boolean) => void): void {
		setIsEditMode(false);
		setCalculatedFieldToEdit(null);
		setNewCalculatedField(null);
		open(true);
	}

	/// One calculated field of any kind: its name, the delete, the id for a
	/// super admin, and the gear that opens the editor able to write this kind —
	/// which list the row sits in is what decides that editor.
	function calculatedFieldRow(cf: CalculatedField, open: (value: boolean) => void): JSX.Element {
		const isUnsync = typeof cf.id !== 'number';
		return <StatusWrapper key={JSON.stringify(cf.id)}>
			<StatusContainer unsync={isUnsync} width='450px'>
				<TextStatusWrapper>
					<InputStatusName
						readOnly={isUnsync ? !mayCreateSettings : !mayUpdateSettings}
						type='text'
						unsync={isUnsync}
						value={cf.field_name}
						nameLength={cf.field_name?.length ?? 0}
						disabled={true}
						onChange={e => { cf.field_name = e.target.value; setCalculatedFields([...calculatedFields]); }}
					/>
				</TextStatusWrapper>
			</StatusContainer>
			<Restricted to={{ objectAction: 'DeleteCompanySettings' }}>
				{isOwner(me) && <PointerDiv>
					<Trash2 color={RedSidely} onClick={_ => {
						cf.deleted = true;
						setCalculatedFields([...calculatedFields]);
					}}
					/>
				</PointerDiv>}
			</Restricted>
			<RestrictedSuperAdmin>{typeof cf.id == 'number' ? <TextButton onClick={() => copyToClipboard(cf.id as number)}>{cf.id}</TextButton> : <p>unsync</p>}</RestrictedSuperAdmin>
			{mayEditCalculatedFields && <SettingIconDiv title={translateToString('color_scale.title')} onClick={() => setMapColorsState({
				isOpen: true,
				title: cf.field_name,
				kind: 'scale',
				options: cf.display_options,
				apply: options => {
					cf.display_options = options;
					setCalculatedFields([...calculatedFields]);
				},
			})}><Palette /></SettingIconDiv>}
			{mayEditCalculatedFields && <SettingIconDiv onClick={() => editCalculatedField(cf, open)}><Settings /></SettingIconDiv>}
		</StatusWrapper>;
	}

	React.useEffect(() => {
		props.setToolBarState({
			bottomRightToolbarComponent: <Restricted to={{ objectAction: 'CreateCompanySettings' }}>
				<SaveButton
					buttonStyle={ButtonStyle.White}
					disabled={!hasDifferences}
					onClick={() => {
						setStatuses(JSON.parse(JSON.stringify(defaultStatuses)));
						setTags(JSON.parse(JSON.stringify(defaultTags)));
						setAdditionalColumns(JSON.parse(JSON.stringify(defaultAdditionalColumns)));
						setAdditionalFieldColumns(JSON.parse(JSON.stringify(defaultAdditionalFieldColumns)));
						setCalculatedFields(JSON.parse(JSON.stringify(defaultCalculatedFields)));
					}}>
					{translateToNode('cancel')}
				</SaveButton>
				<SaveButton
					disabled={!hasDifferences
						|| tags.some(t => (!t.name || t.name.trim().length == 0) && !t.deleted)
						|| statuses.some(s => (!s.name || s.name.trim().length == 0) && !s.deleted)
						|| additionalColumns.some(ac => (!ac.name || ac.name.trim().length == 0 || ((ac.type === 'MultiSelect' || ac.type === 'Select') && (!Array.isArray(ac.data) || ac.data.length == 0 || ac.data.some(n => !n || n.trim().length == 0)))) && !ac.deleted)
					}
					onClick={() => {
						setLoadingState(LoadingStateEnum.LOADING);
						saveChanges(status_differences, tag_differences, colors, props.type, additional_column_differences, additional_field_column_differences, calculated_field_differences)
							.then(fetchDatas)
							.catch(() => {
								setLoadingState(LoadingStateEnum.ERROR);
							});
					}}>
					{translateToNode('save')}
				</SaveButton>
			</Restricted>
		});
	}, [status_differences, tag_differences, additional_column_differences, colors, props.type, defaultStatuses, defaultTags, defaultAdditionalColumns, defaultAdditionalFieldColumns, hasDifferences, calculated_field_differences]);

	async function fetchDatas(): Promise<void> {
		setLoadingState(LoadingStateEnum.LOADING);
		setTags([]);
		setStatuses([]);
		setAdditionalColumns([]);
		setAdditionalFieldColumns([]);
		setDefaultTags([]);
		setDefaultStatuses([]);
		setDefaultAdditionalColumns([]);
		setDefaultAdditionalFieldColumns([]);
		switch (props.type) {
			case TagType.COMPANY: {
				const statuses = await getClientStatuses();
				setStatuses(statuses);
				setDefaultStatuses(JSON.parse(JSON.stringify(statuses)));
				const tags = await getTags(props.type);
				// @ts-expect-error Itags
				setTags(tags);
				setDefaultTags(JSON.parse(JSON.stringify(tags)));
				const all_additional_columns = await getAdditionalColumns();
				const additional_columns = all_additional_columns.filter(ac => ac.type != 'ReportColumn');
				setAdditionalColumnsAtom(JSON.parse(JSON.stringify(all_additional_columns)));
				setAdditionalColumns(additional_columns);
				setDefaultAdditionalColumns(JSON.parse(JSON.stringify(additional_columns)));
				const additionalFieldColumns = (await getAdditionalFieldColumns()).map(t => ({ ...t, id: t.field_id }));
				setAdditionalFieldColumnsAtom(JSON.parse(JSON.stringify(additionalFieldColumns)));
				setAdditionalFieldColumns(additionalFieldColumns);
				setDefaultAdditionalFieldColumns(JSON.parse(JSON.stringify(additionalFieldColumns)));
				setSubSettings(['statuses', 'tags', 'additional_columns', 'calculated_fields', 'report_fields', 'additional_field_columns']);
				const defaultCalculatedFields = await getCalculatedFields();
				setCalculatedFieldsAtom(JSON.parse(JSON.stringify(defaultCalculatedFields)));
				setCalculatedFields(defaultCalculatedFields);
				setDefaultCalculatedFields(JSON.parse(JSON.stringify(defaultCalculatedFields)));
				break;
			}
			case TagType.ORDER: {
				const statuses = await getOrderStatuses();
				setStatuses(statuses);
				setDefaultStatuses(JSON.parse(JSON.stringify(statuses)));
				setSubSettings(['statuses']);
				break;
			}
			case TagType.OPPORTUNITY: {
				const statuses = (await getOpportunityStatuses()).map(s => ({
					name: s.name,
					locked: s.is_primary,
					id: s.id,
					color_code: s.status_color_code,
					probability: s.probability,
					sequence: s.sequence
				}));
				setStatuses(statuses);
				setDefaultStatuses(JSON.parse(JSON.stringify(statuses)));
				const tags = await getTags(props.type);
				// @ts-expect-error Itags
				setTags(tags);
				setDefaultTags(JSON.parse(JSON.stringify(tags)));
				setSubSettings(['statuses', 'tags']);
				break;
			}
			default: {
				const tags = await getTags(props.type);
				// @ts-expect-error Itags
				setTags(tags);
				setDefaultTags(JSON.parse(JSON.stringify(tags)));
				setSubSettings(['tags']);
			}
		}
		setLoadingState(LoadingStateEnum.LOADED);
	}

	React.useEffect(() => {
		fetchDatas();
	}, [props.type]);

	React.useEffect(() => {
		if (newCalculatedField) {
			if (isEditMode) {
				const index = calculatedFields.findIndex(cf => cf.id === newCalculatedField.id);
				if (index !== -1) {
					calculatedFields[index] = newCalculatedField;
					setCalculatedFields([...calculatedFields]);
				}
			}
			else {
				calculatedFields.push(newCalculatedField);
				setCalculatedFields([...calculatedFields]);
			}
		}
	}, [newCalculatedField]);

	React.useEffect(() => {
		if (!isCalculatedFieldsOpen)
			setCalculatedFieldToEdit(null);
	}, [isCalculatedFieldsOpen]);

	const onDragEnd = ({ source, destination }) => {
		if (!destination || !source || source.index === destination.index) return;
		const [removedItem] = statuses.splice(source.index, 1);
		statuses.splice(destination.index, 0, removedItem);
		setStatuses([...statuses].map((s, i) => ({ ...s, sequence: i + 1 })));
	};

	const createAdditionalColumn = React.useCallback(() => {
		const newAdditionalColumn: AdditionalColumn = {
			name: undefined,
			type: 'String',
			id: { unsync: Date.now() },
			data: undefined,
			locked: false,
		};
		setAdditionalColumns((additionalColumns) => [...additionalColumns, newAdditionalColumn]);
	}, []);

	const remainingFields = fields.filter(f => !additionalFieldColumns.some(afc => !afc.deleted && afc.field_id == f.id));
	const createAdditionalFieldColumn = () => {
		const field = remainingFields[0];
		if (!field) return;
		const newAdditionalFieldColumn: AdditionalFieldColumn = {
			field_id: field.id,
			field_name: field.name,
			field_type: field.type,
			ishidded: false,
			id: { unsync: Date.now() }
		};
		setAdditionalFieldColumns((additionalFieldColumns) => [...additionalFieldColumns, newAdditionalFieldColumn]);
	};

	const createTag = React.useCallback(() => {
		const newTag = {
			color: colors[0].colorCode,
			name: undefined,
		};
		setTags((tags) => [...tags, { ...newTag, id: { unsync: Date.now() } }]);
	}, [colors]);

	const createStatus = React.useCallback(() => {
		switch (props.type) {
			case TagType.COMPANY:
			case TagType.ORDER: {
				setStatuses((statuses) => [...statuses, {
					color_code: colors[0].colorCode,
					sequence: statuses.length + 1,
					id: { unsync: Date.now() }
				}]);
				return;
			}
			case TagType.OPPORTUNITY: {
				setStatuses((statuses) => [...statuses, {
					color_code: colors[0].colorCode,
					probability: 50,
					sequence: statuses.length + 1,
					id: { unsync: Date.now() }
				}]);
				return;
			}
			default: return;
		}
	}, [props.type, colors]);

	const changeStatusProbability = (probability: number, id: Id) => () => {
		const status = statuses.find(s => s.id === id);
		if (!status || status.probability === undefined || status.locked) return;
		status.probability += probability;
		if (status.probability > 100 || status.probability < 0) {
			status.probability -= probability;
			return;
		}
		setStatuses([...statuses]);
	};

	if (loadingState === LoadingStateEnum.LOADING) return <ComponentLoader loadingState={loadingState} noBackground />;
	return <>
		<Prompt
			when={hasDifferences}
			message={translateToString('unsaved_changes_leave')}
		/>
		<SystemSettingsWrapper>
			{subSettings.includes('statuses') && <>
				<SystemSettingTitle>{translateToNode(tagTypeToString(props.type) + '_statuses')}</SystemSettingTitle>
				<DragDropContext onDragEnd={onDragEnd}>
					<Droppable droppableId="companies">
						{(provided) => (
							<FlexDiv flow='column' align='stretch' gap='1rem' height={`calc(45px * ${statuses.filter(s => !s.deleted).length} + 1rem * ${statuses.filter(s => !s.deleted).length - 1})`} {...provided.droppableProps} innerRef={provided.innerRef}>
								{statuses.sort((sa, sb) => (sa.sequence ?? 0) - (sb.sequence ?? 0)).map((s, index) => {
									if (s.deleted) return null;
									const isUnsync = typeof s.id !== 'number';
									const isPermitedToChange = isUnsync ? mayCreateSettings : mayUpdateSettings;
									return <React.Fragment key={JSON.stringify(s.id)}>
										<Draggable draggableId={JSON.stringify(s.id)} index={index} isDragDisabled={s.sequence === undefined || !isPermitedToChange}>
											{(provided) => (
												<StatusWrapper innerRef={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
													{s.sequence !== undefined && isPermitedToChange && <DandDDots />}
													<StatusContainer unsync={isUnsync}>
														{s.sequence !== undefined && <div style={{ width: '2em' }}>
															{s.sequence}
														</div>}
														<DropdownColor
															restrictedColors
															autoOptionUp
															disabled={!isPermitedToChange}
															dropdownStyle={{ height: '35px', width: '35px', optionWidth: `${56 * 4}px`, optionHeight: `${56 * 3}px`, optionUp: true }}
															// datalist={colorData}
															name={''}
															selectedValue={s.color_code}
															JSXButton={({ value }) => <>{value?.label}</>}
															onChange={value => {
																if (value.value === s.color_code)
																	return;
																statuses[index].color_code = value.value;
																setStatuses([...statuses]);
															}} />
														<TextStatusWrapper>
															{s.locked ? s.name :
																<InputStatusName
																	readOnly={!isPermitedToChange}
																	nameLength={s.name?.length ?? 0}
																	placeholder={isUnsync ? translateToString('new_status') : undefined}
																	unsync={isUnsync}
																	width='100%'
																	height='fit-content'
																	type='text'
																	onChange={e => {
																		const name = e.target.value;
																		statuses[index].name = name;
																		setStatuses([...statuses]);
																	}}
																	value={s.name}
																	onKeyDownCapture={blurOnEnterPressed} />}
														</TextStatusWrapper>
														{s.probability !== undefined && <StatusProbabilityWrapper locked={s.locked || !isPermitedToChange}>
															{s.probability} %
															{!s.locked && isPermitedToChange && <FlexDiv justify='center' flow='column'>
																<Arrow up isSorted onClick={changeStatusProbability(10, s.id)}><ChevronUp size={11} /></Arrow>
																<Arrow isSorted onClick={changeStatusProbability(-10, s.id)}><ChevronDown size={11} /></Arrow>
															</FlexDiv>}
														</StatusProbabilityWrapper>}
													</StatusContainer>
													{s.locked
														? <Lock><LockKeyhole /></Lock>
														: <Restricted to={{ objectAction: isUnsync ? 'CreateCompanySettings' : 'DeleteCompanySettings' }}>
															<PointerDiv>
																<Trash2 color={RedSidely} onClick={_ => {
																	const sequence = statuses[index].sequence;
																	statuses[index].deleted = true;
																	if (sequence !== undefined) {
																		statuses.forEach(s => {
																			if (s.sequence && s.sequence > sequence)
																				s.sequence -= 1;
																		});
																	}
																	if (isUnsync) {
																		statuses.splice(index, 1);
																	}
																	setStatuses([...statuses]);
																}} />
															</PointerDiv>
														</Restricted>
													}
													<RestrictedSuperAdmin>
														{typeof s.id == 'number' ? <TextButton onClick={() => copyToClipboard(s.id as number)}>{s.id}</TextButton> : <p>unsync</p>}
													</RestrictedSuperAdmin>
												</StatusWrapper>
											)}
										</Draggable>
									</React.Fragment>;
								})}
							</FlexDiv>
						)}
					</Droppable>
				</DragDropContext>
				<Restricted to={{ objectAction: 'CreateCompanySettings' }}>
					<Add onClick={createStatus} />
				</Restricted>
				<br />
				<div />
			</>}
			{subSettings.includes('tags') && <>
				<SystemSettingTitle>{translateToNode(tagTypeToString(props.type) + '_tags')}</SystemSettingTitle>
				<FlexDiv gap='5px' width='100%' flexWrap='wrap'>
					{tags
						.sort((a, b) => {
							if (typeof a.id === 'number' && typeof b.id !== 'number') return -1;
							if (typeof a.id !== 'number' && typeof b.id === 'number') return 1;
							if (typeof a.id !== 'number' && typeof b.id !== 'number') return 0;
							if (a.color && b.color && a.color !== b.color) {
								return a.color.localeCompare(b.color);
							}
							const defaultA = defaultTags.find(dt => dt.id === a.id);
							const defaultB = defaultTags.find(dt => dt.id === b.id);
							if (defaultA && defaultB) return defaultA.name!.localeCompare(defaultB.name!);

							let idA: number;
							if (typeof a.id === 'number') idA = a.id;
							else idA = a.id.unsync;

							let idB: number;
							if (typeof b.id === 'number') idB = b.id;
							else idB = b.id.unsync;
							return idA - idB;
						})
						.map((t, index) => {
							if (t.deleted) return null;
							const isUnsync = typeof t.id !== 'number';
							let internalId: number;
							if (typeof t.id === 'number') {
								internalId = t.id;
							} else {
								internalId = t.id.unsync;
							}
							return <TagContainer unsync={isUnsync} borderColor={t.color} key={`Tag[${internalId}]`}>
								<DropdownColor
									restrictedColors
									disabled={!(isUnsync ? mayCreateSettings : mayUpdateSettings)}
									autoOptionUp
									dropdownStyle={{ height: '20px', width: '20px', optionUp: true }}
									dotSize='20px'
									name={''}
									gallery
									selectedValue={t.color}
									// JSXButton={({ value }) => value?.value ? <Dot color={value.value.colorCode} size='20px' cursor='pointer' /> : <></>}
									onChange={value => {
										if (value.value === t.color)
											return;
										tags[index].color = value.value;
										setTags([...tags]);
									}} />
								<TextTagWrapper>
									<InputTagName
										readOnly={!(isUnsync ? mayCreateSettings : mayUpdateSettings)}
										nameLength={t.name?.length ?? 0}
										placeholder={isUnsync ? translateToString('new_tag') : undefined}
										unsync={isUnsync}
										width='100%'
										height='fit-content'
										type='text'
										onChange={e => {
											const name = e.target.value;
											tags[index].name = name;
											setTags([...tags]);
										}}
										value={t.name}
										onKeyDownCapture={blurOnEnterPressed} />
								</TextTagWrapper>
								<Restricted to={{ objectAction: isUnsync ? 'CreateCompanySettings' : 'DeleteCompanySettings' }}>
									<PointerDiv>
										<Trash2 color={RedSidely} onClick={_ => {
											tags[index].deleted = true;
											if (isUnsync) {
												tags.splice(index, 1);
											}
											setTags([...tags]);
										}} />
									</PointerDiv>
								</Restricted>
								<RestrictedSuperAdmin>
									{typeof t.id == 'number' ? <TextButton onClick={() => copyToClipboard(t.id as number)}>{t.id}</TextButton> : <p>unsync</p>}
								</RestrictedSuperAdmin>
							</TagContainer>
							;
						})}
					<Restricted to={{ objectAction: 'CreateCompanySettings' }}>
						<Add onClick={createTag} />
					</Restricted>
					<br />
				</FlexDiv>
				<br />
			</>}
			{isOwner(me) && <>
				<SystemSettingTitle><Translate id="company.profile_editor.visible_fields" /></SystemSettingTitle>
				<DefaultButton width='100px' onClick={() => setIsCompanyOverviewOpen(true)}><Translate id="edit" /></DefaultButton>
				<CompanyProfile
					columns={{ additionnalColumns: additionalColumns, calculatedFields: calculatedFields, additionalFieldColumns: additionalFieldColumns }}
					isOpen={isCompanyOverviewOpen}
					onClose={() => setIsCompanyOverviewOpen(false)}
				/>
			</>
			}
			{subSettings.includes('additional_columns') && <Restricted to={{ objectAction: 'ReadForm' }} >
				<SystemSettingTitle>{translateToNode('additional_columns')}</SystemSettingTitle>
				{additionalColumns.sort((a, b) => a.name && b.name ? a.name?.localeCompare(b.name) : 0).map((ac, index) => {
					if (ac.deleted) return null;
					const isUnsync = typeof ac.id !== 'number';
					const isPermitedToChange = !additionalColumns[index].locked && (isUnsync ? mayCreateSettings : mayUpdateSettings);
					return <StatusWrapper key={JSON.stringify(ac.id)}>
						<StatusContainer unsync={isUnsync} width='450px'>
							<TextStatusWrapper>
								<InputStatusName
									readOnly={!isPermitedToChange}
									nameLength={ac.name?.length ?? 0}
									placeholder={isUnsync ? translateToString('new_column') : undefined}
									unsync={isUnsync}
									width='100%'
									height='fit-content'
									type='text'
									onChange={e => {
										const name = e.target.value;
										additionalColumns[index].name = name;
										setAdditionalColumns([...additionalColumns]);
									}}
									value={ac.name}
									onKeyDownCapture={blurOnEnterPressed}
								/>
							</TextStatusWrapper>
							{isUnsync ?
								<Dropdown
									readOnly
									autoOptionUp
									dropdownStyle={{
										containerBorder: 'none',
										width: '205px',
										optionWidth: '205px',
									}}
									name='DD-additional-column-type'
									selectedValue={{ label: translateToString('additional_column_types.' + ac.type), value: ac.type }}
									datalist={ADDITIONAL_COLUMN_TYPE_LST.filter(e => e != 'ReportColumn').map(e => ({ label: translateToString('additional_column_types.' + e), value: e }))}
									onChange={value => {
										additionalColumns[index].type = value.value;
										setAdditionalColumns([...additionalColumns]);

									}}
								/> : <div style={{ width: 195, flexShrink: 0 }}>{translateToString('additional_column_types.' + ac.type)}</div>}
						</StatusContainer>
						<RestrictedSuperAdmin>
							<PreventModif locked={additionalColumns[index].locked} onClick={_ => {
								additionalColumns[index].locked = !additionalColumns[index].locked;
								setAdditionalColumns([...additionalColumns]);
							}}>
								<LockKeyhole />
							</PreventModif>
						</RestrictedSuperAdmin>
						{additionalColumns[index].locked && <WarningLock title={translateToString('report_editor.contact_sidely_modify')}><LockKeyhole /></WarningLock>}
						{!additionalColumns[index].locked && <Restricted to={{ objectAction: isUnsync ? 'CreateCompanySettings' : 'DeleteCompanySettings' }}>
							<PointerDiv>
								<Trash2 color={RedSidely} onClick={_ => {
									additionalColumns[index].deleted = true;
									if (isUnsync) {
										additionalColumns.splice(index, 1);
									}
									setAdditionalColumns([...additionalColumns]);
								}}
								/>
							</PointerDiv>
						</Restricted>
						}
						{mapColorsKindOf(ac.type) && <Restricted to={{ objectAction: isUnsync ? 'CreateCompanySettings' : 'UpdateCompanySettings' }}>
							<SettingIconDiv title={translateToString('color_scale.title')} onClick={() => setMapColorsState({
								isOpen: true,
								title: ac.name,
								kind: mapColorsKindOf(ac.type),
								date: ac.type === 'Date',
								options: ac.display_options,
								apply: options => {
									additionalColumns[index].display_options = options as AdditionalColumnsDisplayOptions | undefined;
									setAdditionalColumns([...additionalColumns]);
								},
							})}><Palette /></SettingIconDiv>
						</Restricted>}
						<Restricted to={{ objectAction: isUnsync ? 'CreateCompanySettings' : 'UpdateCompanySettings' }}>
							{(ac.type == 'Select' || ac.type == 'MultiSelect' || ac.type == 'Hours' || isAllowedToEditForms) && <SettingIconDiv onClick={() => setAdditionalColumnSettingState({ isOpen: true, data: ac })}><Settings /></SettingIconDiv>}
						</Restricted>
						<RestrictedSuperAdmin>
							{typeof ac.id == 'number' ? <TextButton onClick={() => copyToClipboard(ac.id as number)}>{ac.id}</TextButton> : <p>unsync</p>}
						</RestrictedSuperAdmin>
					</StatusWrapper>;
				})}
				<Restricted to={{ objectAction: 'CreateCompanySettings' }}>
					<Add onClick={createAdditionalColumn} />
				</Restricted>
				<br />
			</Restricted>}
			{
				subSettings.includes('calculated_fields') && <Restricted to={{ objectAction: 'ReadForm' }} >
					<SystemSettingTitle>{translateToNode('general.calculated_fields')}</SystemSettingTitle>
					{reportFields.map(cf => calculatedFieldRow(cf, setIsCalculatedFieldsOpen))}
					{mayEditCalculatedFields && <Restricted to={{ objectAction: 'CreateCompanySettings' }}>
						<Add onClick={() => createCalculatedField(setIsCalculatedFieldsOpen)} />
					</Restricted>}
					{/* Scores and objective completions are behind the super admin while
						they go out tenant by tenant. Hiding the two lists is enough to
						hold the whole feature back: nothing else creates a scorecard or
						an objective field, and everything downstream — the Score tab, the
						rep rollup, the stored completions — only exists once one does.
						a scorecard's report holds criteria and an objective field's holds
						goal coordinates, not panels: the report editor cannot represent
						either, and its save path would file them as external dataset
						fields. Each kind lists under its own title and its own editor. */}
					<RestrictedSuperAdmin>
						<br />
						<SystemSettingTitle>{translateToNode('scorecards')}</SystemSettingTitle>
						{scorecardFields.map(cf => calculatedFieldRow(cf, setIsScorecardOpen))}
						{mayEditCalculatedFields && <Restricted to={{ objectAction: 'CreateCompanySettings' }}>
							<Add onClick={() => createCalculatedField(setIsScorecardOpen)} />
						</Restricted>}
						<br />
						<SystemSettingTitle>{translateToNode('objective_fields')}</SystemSettingTitle>
						{objectiveFields.map(cf => calculatedFieldRow(cf, setIsObjectiveOpen))}
						{mayEditCalculatedFields && <Restricted to={{ objectAction: 'CreateCompanySettings' }}>
							<Add onClick={() => createCalculatedField(setIsObjectiveOpen)} />
						</Restricted>}
					</RestrictedSuperAdmin>
					<ScorecardEditor
						isOpen={isScorecardOpen}
						setIsOpen={setIsScorecardOpen}
						setCalculatedField={setNewCalculatedField}
						calculatedField={calculatedFieldToEdit as CalculatedFieldAtom}
					/>
					<ObjectiveEditor
						isOpen={isObjectiveOpen}
						setIsOpen={setIsObjectiveOpen}
						setCalculatedField={setNewCalculatedField}
						calculatedField={calculatedFieldToEdit as CalculatedFieldAtom}
					/>
					<CalculatedFieldsCreation
						isOpen={isCalculatedFieldsOpen}
						setIsOpen={setIsCalculatedFieldsOpen}
						setCalculatedField={setNewCalculatedField}
						calculatedField={calculatedFieldToEdit as CalculatedFieldAtom}
					/>
				</Restricted>}
			{subSettings.includes('additional_field_columns') && <Restricted to={{ objectAction: 'ReadForm' }} >
				<SystemSettingTitle>{translateToNode('form_fields')}</SystemSettingTitle>
				{additionalFieldColumns.sort((a, b) => a.field_name.localeCompare(b.field_name)).map((ac, index) => {
					if (ac.deleted) return null;
					const isUnsync = typeof ac.id !== 'number';
					const colorsKind = ac.field_type ? mapColorsKindOf(ac.field_type) : undefined;
					return <StatusWrapper key={JSON.stringify(ac.id)}>
						<StatusContainer unsync={isUnsync} width='450px'>
							{isUnsync ?
								<Dropdown
									readOnly
									autoOptionUp
									dropdownStyle={{
										containerBorder: 'none',
										width: 'calc(450px - 4rem)',
										optionWidth: 'calc(450px - 4rem)',

									}}
									name='DD-additional-column-type'
									selectedValue={{ label: ac.field_name, value: ac.field_id }}
									datalist={remainingFields.map(e => ({ label: e.name, value: e.id }))}
									onChange={value => {
										additionalFieldColumns[index].field_id = value.value;
										if (typeof value.label === 'string') additionalFieldColumns[index].field_name = value.label;
										setAdditionalFieldColumns([...additionalFieldColumns]);

									}}
								/> : ac.field_name}
						</StatusContainer>
						<Restricted to={{ objectAction: isUnsync ? 'CreateCompanySettings' : 'DeleteCompanySettings' }}>
							<PointerDiv>
								<Trash2 color={RedSidely} onClick={_ => {
									additionalFieldColumns[index].deleted = true;
									if (isUnsync) {
										additionalFieldColumns.splice(index, 1);
									}
									setAdditionalFieldColumns([...additionalFieldColumns]);
								}}
								/>
							</PointerDiv>
						</Restricted>
						{!isUnsync && colorsKind && <Restricted to={{ objectAction: 'UpdateCompanySettings' }}>
							<SettingIconDiv title={translateToString('color_scale.title')} onClick={() => setMapColorsState({
								isOpen: true,
								title: ac.field_name,
								kind: colorsKind,
								date: ac.field_type === 'Date',
								options: ac.display_options,
								apply: options => {
									additionalFieldColumns[index].display_options = options;
									setAdditionalFieldColumns([...additionalFieldColumns]);
								},
							})}><Palette /></SettingIconDiv>
						</Restricted>}
						<RestrictedSuperAdmin>
							{typeof ac.id == 'number' ? <TextButton onClick={() => copyToClipboard(ac.id as number)}>{ac.id}</TextButton> : <p>unsync</p>}
						</RestrictedSuperAdmin>
					</StatusWrapper>;
				})}
				<Restricted to={{ objectAction: 'CreateCompanySettings' }}>
					{remainingFields.length > 0 && <Add onClick={createAdditionalFieldColumn} />}
				</Restricted>
				<br />

			</Restricted>}
		</SystemSettingsWrapper>
		<MapColorsPopup
			isOpen={mapColorsState.isOpen}
			title={mapColorsState.title}
			kind={mapColorsState.kind}
			date={mapColorsState.date}
			options={mapColorsState.options}
			onClose={() => setMapColorsState({ isOpen: false })}
			onSave={options => mapColorsState.apply?.(options)}
		/>
		<AdditionalColumnSettingPopup
			isOpen={additionalColumnSettingState.isOpen}
			onClickOut={() => setAdditionalColumnSettingState({ isOpen: false })}
			additionalColumn={additionalColumnSettingState.data}
			onSave={newAdditionalColumn => {
				const index = additionalColumns.findIndex(ac => ac.id === newAdditionalColumn.id);
				if (index === -1) return;
				additionalColumns[index] = newAdditionalColumn;
				setAdditionalColumns([...additionalColumns]);
			}}
		/>
	</>;
}

function AdditionalColumnSettingPopup(props: { isOpen: boolean, onClickOut: () => void, additionalColumn?: AdditionalColumn, onSave: (newAdditionalColumn: AdditionalColumn) => void }) {
	const { additionalColumn } = props;
	const selectOptions = Array.isArray(additionalColumn?.data) ? additionalColumn?.data : undefined;
	const [metadatas, setMetadatas] = React.useState<string[]>(selectOptions ? [...selectOptions] : ['']);
	const [displayOptions, setDisplayOptions] = React.useState<AdditionalColumnsDisplayOptions | undefined>(additionalColumn?.display_options);
	const [metadataTextArea, setMetadataTextArea] = React.useState<string>(selectOptions ? selectOptions.map(e => `${e}`).join('\n') : '');
	const [fields, setFields] = React.useState<Field[]>([]);
	const [linkedField, setLinkedField] = React.useState<Field | undefined>(fields.find(f => f.id === additionalColumn?.field_id));
	const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
	const isAllowedToEditForms = useEditFormsPermission();

	React.useEffect(() => {
		getFields().then(setFields);
	}, []);

	React.useEffect(() => {
		const selectOptions = Array.isArray(additionalColumn?.data) ? additionalColumn?.data : undefined;
		setMetadatas(selectOptions ? [...selectOptions] : ['']);
		setMetadataTextArea(selectOptions ? selectOptions.map(e => `${e}`).join('\n') : '');
		setLinkedField(fields.find(f => f.id === additionalColumn?.field_id));
		setDisplayOptions(additionalColumn?.display_options);
	}, [additionalColumn, props.isOpen]);

	React.useEffect(() => {
		setLinkedField(fields.find(f => f.id === additionalColumn?.field_id));
	}, [fields]);

	return <Popup isOpen={props.isOpen} onClickOut={props.onClickOut} popupMode={PopupMode.Details}>
		{additionalColumn &&
			<PopupCreation
				title={additionalColumn.name ?? translateToString('new_column')}
				messageButton={translateToString('save')}
				onSubmit={() => {
					const validate_metadatas = metadatas.length !== 0 && metadatas.every(md => md.trim().length !== 0);
					const data = additionalColumn.type === 'Select' || additionalColumn.type === 'MultiSelect'
						? (validate_metadatas ? metadatas : undefined)
						: undefined;
					props.onSave({ ...additionalColumn, data, field_id: linkedField?.id, display_options: displayOptions } as AdditionalColumn);
					props.onClickOut();
				}}
				canValidate={!props.additionalColumn?.locked && metadatas.length !== 0 && metadatas.every(md => md.trim().length !== 0)
					|| ((linkedField !== undefined && linkedField.id !== additionalColumn?.field_id) || linkedField === undefined && linkedField !== additionalColumn.field_id)
					|| (additionalColumn.type === 'Hours' && JSON.stringify(displayOptions?.opening_hours_rule ?? null) !== JSON.stringify(additionalColumn.display_options?.opening_hours_rule ?? null))}
			>
				<CreationPopupContainer gap='1em' padding='15px 8px'>
					{isAllowedToEditForms &&
						<Dropdown
							name='DD-additional-column-links'
							label={translateToString('field_settings.link_to_form_field')}
							datalist={fields.map(e => ({ label: e.name, value: e.id }))}
							selectedValue={linkedField ? { label: linkedField.name, value: linkedField.id } : undefined}
							onChange={value => {
								setLinkedField(fields.find(f => f.id === value.value));
							}}
							cancellable
							readOnly
							dropdownStyle={{
								width: '20vw',
								optionWidth: '20vw',
								labelUp: true,
								margin: '0 0 1em 0'
							}}
						/>
					}
					{additionalColumn.type === 'Hours' && <OpeningHoursRuleEditor
						rule={displayOptions?.opening_hours_rule}
						onChange={rule => setDisplayOptions(prev => ({ ...prev, opening_hours_rule: rule }))}
					/>}
					{(additionalColumn.type === 'Select' || additionalColumn.type === 'MultiSelect') && <>
						<label style={{
							fontSize: '12px',
							color: DarkGreySidely2,
						}}>
							{props.additionalColumn?.locked ? <div style={{ color: RedSidely }} >{translateToString('report_editor.contact_sidely_modify')}</div>
								: <div>{translateToString('form_editor.modify_multiple_values')}</div>}
							{!props.additionalColumn?.locked && <textarea ref={textAreaRef} defaultValue={metadataTextArea}
								style={{
									width: '100%',
									border: `1px solid ${BorderColor}`,
									borderRadius: '5px',
									fontSize: '12px',
									padding: '9px 12px',
									color: DarkGreySidely2,
								}}
								onChange={e => {
									if (textAreaRef.current) {
										textAreaRef.current.style.height = '';
										textAreaRef.current.style.height = textAreaRef.current.scrollHeight + 3 + 'px';
									}
									const newMetadatas = e.target.value.split('\n').map(e => e.trim()).filter(e => e.length > 0);
									setDisplayOptions(prev => {
										if (!prev?.pin_styles) return prev;
										const newPinStyles: Record<string, PinStyle> = {};
										newMetadatas.forEach((newKey, i) => {
											const oldKey = metadatas[i];
											if (oldKey && prev.pin_styles![oldKey]) {
												newPinStyles[newKey] = prev.pin_styles![oldKey];
											}
										});
										return { ...prev, pin_styles: newPinStyles };
									});
									setMetadatas(newMetadatas);
								}} />}
						</label>
						{metadatas?.map((e, i) => <SelectMetaData
							locked={!!props.additionalColumn?.locked}
							title={translateToString('values')}
							value={e}
							key={`selectedMetaData[${i}]`}
							first={i === 0}
							onDelete={() => {
								const keyToDelete = metadatas[i];
								setDisplayOptions(prev => {
									if (!prev?.pin_styles) return prev;
									const { [keyToDelete]: _, ...rest } = prev.pin_styles;
									return {
										...prev,
										pin_styles: rest
									};
								});
								setMetadatas(md => {
									md.splice(i, 1);
									return [...md];
								});
							}}
							onChange={value => {
								if (value.data) {
									const oldKey = metadatas[i];
									const newKey = value.data;
									setDisplayOptions(prev => {
										if (!prev?.pin_styles) return prev;
										const { [oldKey]: existingStyle, ...rest } = prev.pin_styles;
										return {
											...prev,
											pin_styles: existingStyle
												? { ...rest, [newKey]: existingStyle }
												: rest,
										};
									});
									setMetadatas(md => {
										md[i] = newKey;
										return [...md];
									});
								}
								if (value.pinStyle) {
									setDisplayOptions(prev => {
										const merged: PinStyle = { ...prev?.pin_styles?.[e], ...value.pinStyle! };
										if ('logo' in value.pinStyle! && value.pinStyle!.logo === undefined) {
											delete merged.logo;
										}
										const newPinStyles = { ...prev?.pin_styles };
										if (!merged.logo && !merged.color) {
											delete newPinStyles[e];
										} else {
											newPinStyles[e] = merged;
										}
										return {
											...prev,
											pin_styles: newPinStyles,
										};
									});
								}
							}}
							color
							selectedPinStyle={displayOptions?.pin_styles ? displayOptions?.pin_styles[metadatas[i]] : undefined}
						/>) ?? <></>}
						{!props.additionalColumn?.locked && <Add onClick={() => setMetadatas(md => {
							md.push('');
							return [...md];
						})} />}
					</>}

				</CreationPopupContainer>

			</PopupCreation>}
	</Popup>;
}
